import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from '@itsrever/design-system'
import WarehousesApi from '@/infrastructure/api/warehouses'
import {
  captureWarehousesEvent,
  WarehousesEvent
} from '@/infrastructure/posthog'
import {
  RoutingRule,
  splitDefault,
  toConditions,
  WarehouseUpdate
} from '@/domain/warehouses'

const WAREHOUSES_KEY = ['groupWarehouses']

const warehousesKey = (group?: string) => [...WAREHOUSES_KEY, 'list', group]
const routingKey = (group?: string) => [...WAREHOUSES_KEY, 'routing', group]

/** `retry`: retries after a failure; React Query's default (3) when left out. */
export function useGroupWarehouses(
  group?: string,
  { retry }: { retry?: number } = {}
) {
  const query = useQuery({
    queryKey: warehousesKey(group),
    queryFn: ({ signal }) =>
      WarehousesApi.findGroupWarehouses(group ?? '', signal),
    enabled: !!group,
    retry
  })
  return {
    warehouses: query.data ?? [],
    loadingWarehouses: query.isLoading,
    warehousesError: query.isError,
    reloadWarehouses: query.refetch
  }
}

export function useGroupRouting(group?: string) {
  const query = useQuery({
    queryKey: routingKey(group),
    queryFn: ({ signal }) =>
      WarehousesApi.findGroupRouting(group ?? '', signal),
    enabled: !!group
  })
  return {
    routing: query.data ?? { rules: [], overrides: {} },
    loadingRouting: query.isLoading,
    routingError: query.isError,
    reloadRouting: query.refetch
  }
}

// Condition fields only: the values are merchant data. A rule kept as written counts as 'custom'.
const rulesSummary = (rules: RoutingRule[]) => {
  const { rules: conditional, fallback } = splitDefault(rules)
  const fields = conditional.flatMap(
    (rule) =>
      toConditions(rule.whens)?.map((condition) => condition.field) ?? [
        'custom'
      ]
  )
  return {
    ruleCount: conditional.length,
    hasDefaultRule: !!fallback,
    conditionFields: [...new Set(fields)]
  }
}

// Every write refetches what it changed: the saved warehouse id comes from the server, and the
// usage counts on screen are derived from the routing, so both must reflect the stored state.
function useWarehousesMutation<Input, Result>(
  mutationFn: (input: Input) => Promise<Result>,
  keys: unknown[][],
  messages: { success: string; error: string },
  tracking: {
    event: WarehousesEvent
    properties: (input: Input) => { group: string } & Record<string, unknown>
  }
) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn,
    onSuccess: (_, input) => {
      captureWarehousesEvent(tracking.event, {
        ...tracking.properties(input),
        succeeded: true
      })
      toast({ text: t(messages.success), variant: 'success' })
      keys.forEach((queryKey) => queryClient.invalidateQueries({ queryKey }))
    },
    onError: (error, input) => {
      captureWarehousesEvent(tracking.event, {
        ...tracking.properties(input),
        succeeded: false
      })
      console.warn(error)
      toast({ text: t(messages.error), variant: 'error' })
    }
  })
  return { run: mutation.mutate, running: mutation.isPending }
}

export function useSaveWarehouse(group: string) {
  const { run, running } = useWarehousesMutation(
    (warehouse: WarehouseUpdate) =>
      WarehousesApi.saveGroupWarehouse(group, warehouse),
    [warehousesKey(group)],
    { success: 'warehouses.save_success', error: 'warehouses.save_error' },
    {
      event: 'Warehouse saved',
      properties: (warehouse) => ({ group, created: !warehouse.id })
    }
  )
  return { saveWarehouse: run, savingWarehouse: running }
}

export function useDeleteWarehouse(group: string) {
  const { run, running } = useWarehousesMutation(
    (id: string) => WarehousesApi.deleteGroupWarehouse(group, id),
    [warehousesKey(group)],
    { success: 'warehouses.delete_success', error: 'warehouses.delete_error' },
    { event: 'Warehouse deleted', properties: () => ({ group }) }
  )
  return { deleteWarehouse: run, deletingWarehouse: running }
}

export function useSaveGroupRules(group: string) {
  const { run, running } = useWarehousesMutation(
    (rules: RoutingRule[]) => WarehousesApi.saveGroupRules(group, rules),
    [routingKey(group)],
    {
      success: 'warehouses.rules.save_success',
      error: 'warehouses.rules.save_error'
    },
    {
      event: 'Warehouse rules saved',
      properties: (rules) => ({ group, level: 'group', ...rulesSummary(rules) })
    }
  )
  return { saveGroupRules: run, savingGroupRules: running }
}

export function useSaveStoreRules(group: string) {
  const { run, running } = useWarehousesMutation(
    ({ ecommerceId, rules }: { ecommerceId: string; rules: RoutingRule[] }) =>
      WarehousesApi.saveStoreRules(ecommerceId, rules),
    [routingKey(group)],
    {
      success: 'warehouses.rules.save_success',
      error: 'warehouses.rules.save_error'
    },
    {
      event: 'Warehouse rules saved',
      properties: ({ ecommerceId, rules }) => ({
        group,
        level: 'store',
        ecommerceId,
        ...rulesSummary(rules)
      })
    }
  )
  return { saveStoreRules: run, savingStoreRules: running }
}

export function useResetStoreRules(group: string) {
  const { run, running } = useWarehousesMutation(
    (ecommerceId: string) => WarehousesApi.resetStoreRules(ecommerceId),
    [routingKey(group)],
    {
      success: 'warehouses.rules.reset_success',
      error: 'warehouses.rules.reset_error'
    },
    {
      event: 'Warehouse store rules reset',
      properties: (ecommerceId) => ({ group, ecommerceId })
    }
  )
  return { resetStoreRules: run, resettingStoreRules: running }
}
